import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Mail, Lock, Bell, LogOut, Trash2, Shield, Loader2, Check, Eye, EyeOff, Crown, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import PremiumModal from '../components/PremiumModal';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

import API_URL from '../config/api';
import SEOHead from '../components/SEOHead';

export default function Settings() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showPremium, setShowPremium] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  // Email state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState({
    new_matches: true,
    new_messages: true,
    likes: true,
    system_notifications: true,
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (err) {
      // Non-critical: notifications will use component defaults if fetch fails
      setNotifications({ new_matches: true, messages: true, likes: true, promotions: false });
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // ── Email Change ──
  const handleEmailChange = async () => {
    if (!newEmail || !emailPassword) {
      toast.error(t('settings.fill_all_fields'));
      return;
    }
    setSavingEmail(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-email`, {
        new_email: newEmail,
        password: emailPassword,
      }, { withCredentials: true });
      toast.success(t('settings.email_updated'));
      setNewEmail('');
      setEmailPassword('');
      setExpandedSection(null);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.email_update_failed'));
    } finally {
      setSavingEmail(false);
    }
  };

  // ── Password Change ──
  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t('settings.fill_all_fields'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('settings.passwords_not_match'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('settings.password_min_length'));
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }, { withCredentials: true });
      toast.success(t('settings.password_updated'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setExpandedSection(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.password_update_failed'));
    } finally {
      setSavingPassword(false);
    }
  };

  // ── Notification Save ──
  const handleNotificationSave = async (key, value) => {
    const updated = { ...notifications, [key]: value };
    setNotifications(updated);
    setSavingNotifications(true);
    try {
      await axios.put(`${API_URL}/api/settings/notifications`, updated, { withCredentials: true });
      toast.success(t('settings.notifications_updated'));
    } catch (err) {
      setNotifications(notifications); // revert
      toast.error(t('settings.notifications_update_failed'));
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const plan = user?.current_plan || 'free';
  const isGoogleAuth = user?.auth_provider === 'google';

  const inputCls = 'w-full px-4 py-3 bg-white border border-[#E5E3DB] rounded-xl text-sm text-[#2D2D2A] placeholder-[#9C9C97] focus:outline-none focus:border-[#6E1A24] focus:ring-2 focus:ring-[#6E1A24]/10 transition-all';

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="settings-page">
      <SEOHead title="Settings" noindex path="/settings" />
      <Navigation />

      <div className="pt-24 pb-28 px-4 sm:px-6">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#6E1A24]/10 flex items-center justify-center">
              <SettingsIcon className="w-6 h-6 text-[#6E1A24]" />
            </div>
            <div>
              <h1 className="text-2xl font-serif font-medium text-[#2D2D2A]">{t('settings.title')}</h1>
              <p className="text-sm text-[#6C6C67]">{t('settings.manage_account')}</p>
            </div>
          </div>

          <div className="space-y-3">

            {/* ─── Email ─── */}
            <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
              <button
                onClick={() => toggleSection('email')}
                className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-[#FAF9F6] transition-colors"
                data-testid="settings-email-section"
              >
                <div className="w-10 h-10 rounded-xl bg-[#6E1A24]/8 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#6E1A24]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#2D2D2A] text-sm">Email</p>
                  <p className="text-xs text-[#6C6C67] truncate">{user?.email}</p>
                </div>
              </button>

              {expandedSection === 'email' && (
                <div className="px-5 pb-5 pt-1 border-t border-[#E5E3DB] space-y-3">
                  {isGoogleAuth ? (
                    <p className="text-sm text-[#6C6C67] py-2">{t('settings.google_email_managed')}</p>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-[#9C9C97] mb-1">{t('settings.new_email_label')}</label>
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className={inputCls}
                          placeholder={t('settings.new_email_placeholder')}
                          data-testid="settings-new-email"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#9C9C97] mb-1">{t('settings.confirm_with_password')}</label>
                        <input
                          type="password"
                          value={emailPassword}
                          onChange={(e) => setEmailPassword(e.target.value)}
                          className={inputCls}
                          placeholder={t('settings.enter_password_placeholder')}
                          data-testid="settings-email-password"
                        />
                      </div>
                      <Button
                        onClick={handleEmailChange}
                        disabled={savingEmail || !newEmail || !emailPassword}
                        className="bg-[#6E1A24] text-white rounded-xl px-5 py-2.5 text-sm font-semibold hover:bg-[#8A2331] transition-all w-full flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="settings-save-email"
                      >
                        {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {t('settings.update_email')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ─── Password ─── */}
            <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
              <button
                onClick={() => toggleSection('password')}
                className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-[#FAF9F6] transition-colors"
                data-testid="settings-password-section"
              >
                <div className="w-10 h-10 rounded-xl bg-[#6E1A24]/8 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-[#6E1A24]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#2D2D2A] text-sm">{t('settings.password_label')}</p>
                  <p className="text-xs text-[#6C6C67]">{isGoogleAuth ? t('settings.managed_by_google') : t('settings.change_password')}</p>
                </div>
              </button>

              {expandedSection === 'password' && (
                <div className="px-5 pb-5 pt-1 border-t border-[#E5E3DB] space-y-3">
                  {isGoogleAuth ? (
                    <p className="text-sm text-[#6C6C67] py-2">{t('settings.google_password_managed')}</p>
                  ) : (
                    <>
                      <div className="relative">
                        <label className="block text-xs font-medium text-[#9C9C97] mb-1">{t('settings.current_password_label')}</label>
                        <input
                          type={showCurrentPw ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className={inputCls + ' pr-12'}
                          placeholder={t('settings.current_password_placeholder')}
                          data-testid="settings-current-password"
                        />
                        <button onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-7 text-[#9C9C97] hover:text-[#6C6C67]">
                          {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="relative">
                        <label className="block text-xs font-medium text-[#9C9C97] mb-1">{t('settings.new_password_label')}</label>
                        <input
                          type={showNewPw ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className={inputCls + ' pr-12'}
                          placeholder={t('settings.new_password_placeholder')}
                          data-testid="settings-new-password"
                        />
                        <button onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-7 text-[#9C9C97] hover:text-[#6C6C67]">
                          {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#9C9C97] mb-1">{t('settings.confirm_password_label')}</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className={inputCls}
                          placeholder={t('settings.confirm_password_placeholder')}
                          data-testid="settings-confirm-password"
                        />
                      </div>
                      <Button
                        onClick={handlePasswordChange}
                        disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
                        className="bg-[#6E1A24] text-white rounded-xl px-5 py-2.5 text-sm font-semibold hover:bg-[#8A2331] transition-all w-full flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="settings-save-password"
                      >
                        {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {t('settings.update_password')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ─── Notifications ─── */}
            <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
              <button
                onClick={() => toggleSection('notifications')}
                className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-[#FAF9F6] transition-colors"
                data-testid="settings-notifications-section"
              >
                <div className="w-10 h-10 rounded-xl bg-[#6E1A24]/8 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5 text-[#6E1A24]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#2D2D2A] text-sm">{t('settings.notifications')}</p>
                  <p className="text-xs text-[#6C6C67]">{t('settings.manage_notifications')}</p>
                </div>
              </button>

              {expandedSection === 'notifications' && (
                <div className="px-5 pb-5 pt-1 border-t border-[#E5E3DB] space-y-4">
                  {[
                    { key: 'new_matches', label: t('settings.new_matches'), desc: t('settings.new_matches_desc') },
                    { key: 'new_messages', label: t('settings.new_messages'), desc: t('settings.new_messages_desc') },
                    { key: 'likes', label: t('settings.likes_label'), desc: t('settings.likes_desc') },
                    { key: 'system_notifications', label: t('settings.system_label'), desc: t('settings.system_desc') },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between py-1" data-testid={`notification-${item.key}`}>
                      <div>
                        <p className="text-sm font-medium text-[#2D2D2A]">{item.label}</p>
                        <p className="text-xs text-[#6C6C67]">{item.desc}</p>
                      </div>
                      <Switch
                        checked={notifications[item.key]}
                        onCheckedChange={(val) => handleNotificationSave(item.key, val)}
                        disabled={savingNotifications}
                        data-testid={`notification-toggle-${item.key}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ─── Verification ─── */}
            <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
              <div className="px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#2D6A4F]/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-[#2D6A4F]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#2D2D2A] text-sm">{t('settings.verification')}</p>
                  <p className="text-xs text-[#6C6C67]">
                    {user?.is_verified ? t('settings.verified_desc') : t('settings.not_verified')}
                  </p>
                </div>
                {user?.is_verified ? (
                  <span className="text-xs font-semibold text-[#2D6A4F] bg-[#2D6A4F]/10 px-3 py-1 rounded-full" data-testid="settings-verified-badge">{t('profile.verified')}</span>
                ) : (
                  <span className="text-xs text-[#9C9C97]">{t('settings.pending')}</span>
                )}
              </div>
            </div>

            {/* ─── Subscription ─── */}
            <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
              <button
                onClick={() => plan === 'free' && setShowPremium(true)}
                className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-[#FAF9F6] transition-colors"
                data-testid="settings-subscription"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  plan === 'gold' ? 'bg-[#D4AF37]' : plan === 'premium' ? 'bg-[#6E1A24]' : 'bg-[#E5E3DB]'
                }`}>
                  {plan === 'gold' ? (
                    <Crown className="w-5 h-5 text-white" />
                  ) : plan === 'premium' ? (
                    <Crown className="w-5 h-5 text-white" />
                  ) : (
                    <User className="w-5 h-5 text-[#6C6C67]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#2D2D2A] text-sm">
                    {plan === 'gold' ? t('settings.gold_plan') : plan === 'premium' ? t('settings.premium_plan') : t('settings.free_plan')}
                  </p>
                  <p className="text-xs text-[#6C6C67]">
                    {plan === 'free' ? t('settings.upgrade_desc') : t('profile.premium_features_active')}
                  </p>
                </div>
                {plan === 'free' && (
                  <span className="text-xs font-semibold text-[#6E1A24] bg-[#6E1A24]/10 px-3 py-1 rounded-full">{t('common.upgrade')}</span>
                )}
              </button>
            </div>

            {/* ─── Danger Zone ─── */}
            <div className="pt-4 space-y-3">
              <button
                onClick={handleLogout}
                className="w-full px-5 py-4 bg-white rounded-2xl border border-[#E5E3DB] flex items-center gap-4 text-left hover:bg-red-50 hover:border-red-200 transition-colors"
                data-testid="settings-logout"
              >
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <LogOut className="w-5 h-5 text-red-500" />
                </div>
                <p className="font-medium text-red-600 text-sm">{t('settings.logout')}</p>
              </button>

              <button
                className="w-full px-5 py-3 flex items-center justify-center gap-2 text-[#9C9C97] text-xs hover:text-red-400 transition-colors"
                data-testid="settings-delete-account"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t('settings.delete_account')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <PremiumModal
        isOpen={showPremium}
        onClose={() => setShowPremium(false)}
        onUpgradeSuccess={async () => await refreshUser()}
        currentPlan={plan}
      />
    </div>
  );
}
